/**
 * Same catalogue map as backend-v2/src/shared/utils/defect-display-language.ts.
 * Inspect cannot compile that file inside its own TypeScript project.
 */
export const ENGLISH_CATALOGUE_LANGUAGE = 'ENG';

const CATALOGUE_LANGUAGE_BY_LOCALE: Readonly<Record<string, string>> = {
  en: ENGLISH_CATALOGUE_LANGUAGE,
  'zh-Hans': 'zh-Hans',
  tr: 'tr',
  hi: 'hi',
};

export interface DefectLanguageLabels {
  language: string;
  codeDescription?: string;
  categoryDescription?: string;
}

/**
 * Resolves the catalogue language code for a UI locale.
 * Unknown locales fall back to English so a missing preference never picks another language.
 */
export function catalogueLanguageForLocale(locale: string | null | undefined): string {
  const normalized = locale?.trim() ?? '';
  if (!normalized) {
    return ENGLISH_CATALOGUE_LANGUAGE;
  }
  return CATALOGUE_LANGUAGE_BY_LOCALE[normalized] ?? ENGLISH_CATALOGUE_LANGUAGE;
}

/**
 * Picks the catalogue row for the locale, then the English row.
 * Returns undefined when neither exists so the caller can show the defect code.
 */
export function selectDefectLanguage<T extends { language: string }>(
  languages: readonly T[] | null | undefined,
  locale: string | null | undefined
): T | undefined {
  if (!languages?.length) {
    return undefined;
  }
  const catalogueLanguage = catalogueLanguageForLocale(locale);
  const preferred = languages.find((row) => row.language === catalogueLanguage);
  if (preferred) {
    return preferred;
  }
  if (catalogueLanguage === ENGLISH_CATALOGUE_LANGUAGE) {
    return undefined;
  }
  return languages.find((row) => row.language === ENGLISH_CATALOGUE_LANGUAGE);
}

/**
 * Search text for one description field: the displayed language and English.
 */
export function defectLabelSearchText(
  languages: readonly DefectLanguageLabels[] | null | undefined,
  locale: string | null | undefined,
  field: 'codeDescription' | 'categoryDescription'
): string {
  const selected = selectDefectLanguage(languages, locale);
  const english = languages?.find((row) => row.language === ENGLISH_CATALOGUE_LANGUAGE);
  const values = [selected?.[field], english?.[field]]
    .map((value) => value?.trim() ?? '')
    .filter((value) => value.length > 0);
  return [...new Set(values)].join(' ');
}
